import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Form, FormFeedback, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Col } from 'reactstrap';
import { createSelector } from 'reselect';
import { setSelectPathModalKind, toggleFacebookModal, toggleFeedConnectionLoader, toggleFeedConnectionModal, toggleSelectPathModal } from '../../../slices/modals/reducer';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import CustomSelect from '../../../Components/Common/CustomSelect';
import classnames from 'classnames';
import { availabilityOption, conditionOption, countryCurrencyCodes, intervalOption } from '../../../common/facebook';
import { createFacebookProperties, updateFacebookProperties } from '../../../slices/campaigns/facebook/thunk';
import { getFeeds } from '../../../slices/feeds/thunk';
import { setSelectedFacebookProperty } from '../../../slices/campaigns/reducer';
import { setFeedsFirstLoad, setFeedsHasMore, setFeedsLoading, setFeedsPage, setFeedsShowLoader, setSelectedPath } from '../../../slices/feeds/reducer';

function FacebookModal() {
  const dispatch = useDispatch();

  const selectStates = createSelector(state => state, state => ({
    modalState: state.Modal.facebookModal,
    facebookId: state.Campaign.facebookId,
    isFacebookPropertiesSet: state.Campaign.isFacebookPropertiesSet,
    id: state.Campaign.id,
    title: state.Campaign.title,
    description: state.Campaign.description,
    availability: state.Campaign.availability,
    condition: state.Campaign.condition,
    price: state.Campaign.price,
    link: state.Campaign.link,
    image_link: state.Campaign.image_link,
    // brand: state.Campaign.brand,
    interval: state.Campaign.interval,
    limit: state.Campaign.limit,
    currentVersion: state.Campaign.currentVersion,
    connectedFeed: state.Campaign.connectedFeed,
  }));
  const {
    modalState,
    facebookId,
    isFacebookPropertiesSet,
    id,
    title,
    description,
    availability,
    condition,
    price,
    link,
    image_link,
    // brand,
    interval,
    limit,
    currentVersion,
    connectedFeed
  } = useSelector(selectStates);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: {
        value: null,
        isFeed: false,
      },
      title: {
        value: '',
        isFeed: false,
      },
      description: {
        value: '',
        isFeed: false,
      },
      availability: {
        ...availabilityOption[0],
        isFeed: false,
      },
      condition: {
        ...conditionOption[0],
        isFeed: false,
      },
      price: {
        value: '',
        isFeed: false,
      },
      link: {
        value: '',
        isFeed: false,
      },
      image_link: {
        value: '',
        isFeed: false,
      },
      // brand: {
      //   value: '',
      //   isFeed: false
      // },
      interval: intervalOption[0],
      exportType: 'image',
      limit: 5,
    },
    validationSchema: Yup.object().shape({
      id: Yup.object().shape({
        value: Yup.mixed().nullable(),
        isFeed: Yup.boolean()
      }),
      title: Yup.object().shape({
        value: Yup.string().required('Please enter title'),
        isFeed: Yup.boolean()
      }),
      description: Yup.object().shape({
        value: Yup.string().required('Please enter description'),
        isFeed: Yup.boolean()
      }),
      availability: Yup.object().shape({
        name: Yup.string().test(
          'is-valid-availability',
          'Availability must be one of these: in stock, out of stock',
          (value) => {
            if (!value) return false;
            return availabilityOption.some(item => item.name === value)
          }
        ).required('Please select availability'),
        value: Yup.string().required('Please select availability'),
        isFeed: Yup.boolean()
      }),
      condition: Yup.object().shape({
        name: Yup.string().test(
          'is-valid-condition',
          'Condition must be one of these: new, refurbished, used',
          (value) => {
            if (!value) return false;
            return conditionOption.some(item => item.name === value);
          }
        ).required('Please select condition'),
        value: Yup.string().required('Please select condition'),
        isFeed: Yup.boolean()
      }),
      price: Yup.object().shape({
        value: Yup.string().matches(
          /^[0-9]+(\.[0-9]{1,2})?\s([A-Z]{3})$/,
          'Invalid price format. Example: 9.99 EUR'
        ).test(
          'is-valid-currency',
          'Invalid currency code.',
          value => {
            if (!value) return false;
            const currencyCode = value.split(' ')[1];
            return countryCurrencyCodes.includes(currencyCode);
          }
        ).required('Price is required'),
        isFeed: Yup.boolean()
      }),
      link: Yup.object().shape({
        value: Yup.string().matches(/^(http:\/\/|https:\/\/)/, 'The URL must start with http:// or https://')
          .url('Please enter a valid URL')
          .required('The URL is required'),
        isFeed: Yup.boolean()
      }),
      // image_link: Yup.object().shape({
      //   value: Yup.string().matches(/^(http:\/\/|https:\/\/)/, 'The URL must start with http:// or https://')
      //     .url('Please enter a valid URL')
      //     .required('The URL is required'),
      //   isFeed: Yup.boolean()
      // }),
      // brand: Yup.object().shape({
      //   value: Yup.string().required('Please enter brand'),
      //   isFeed: Yup.boolean()
      // }),
      interval: Yup.object().shape({
        name: Yup.string().required('Please select interval'),
        value: Yup.number().required('Please select interval'),
      }),
      limit: Yup.string().required('Please select limit '),
    }),
    onSubmit: async (values, { resetForm }) => {
      let res;
      if (!facebookId) {
        // Create
        res = await dispatch(createFacebookProperties(values));
      } else {
        // Update
        res = await dispatch(updateFacebookProperties(values));
      }
      closeModal();
    }
  });

  useEffect(() => {
    if (id) {
      validation.setValues({
        ...validation.values,
        id: id,
      });
    } 
  }, [id]);

  useEffect(() => {
    validation.setValues({
      ...validation.values,
      title: title,
    });
  }, [title]);

  useEffect(() => {
    validation.setValues({
      ...validation.values,
      description: description,
    });
  }, [description]);

  useEffect(() => {
    validation.setValues({
      ...validation.values,
      availability: availability,
    });
  }, [availability]);

  useEffect(() => {
    validation.setValues({
      ...validation.values,
      condition: condition,
    });
  }, [condition]);

  useEffect(() => {
    validation.setValues({
      ...validation.values,
      price: price,
    });
  }, [price]);

  useEffect(() => {
    validation.setValues({
      ...validation.values,
      link: link,
    });
  }, [link]);

  useEffect(() => {
    validation.setValues({
      ...validation.values,
      image_link: image_link,
    });
  }, [image_link]);

  useEffect(() => {
    let validationObj = {
      ...validation.values,
    };
    if (typeof interval === 'object') {
      validationObj.interval = interval;
    } else {
      validationObj.interval = { value: interval, name: intervalOption.find(item => item.value === interval)?.name };
    }
    validation.setValues(validationObj);
  }, [interval]);

  useEffect(() => {
    validation.setValues({
      ...validation.values,
      limit: limit,
    });
  }, [limit]);

  useEffect(() => {
    let validationObj = {
      title: title,
      description: description,
      availability: availability,
      condition: condition,
      price: price,
      link: link,
      image_link: image_link,
      limit: limit
    };
    if (typeof interval === 'object') {
      validationObj.interval = interval;
    } else {
      validationObj.interval = { value: interval, name: intervalOption.find(item => item.value === interval)?.name };
    }
    if (id) {
      validationObj.id = id;
    }
    validation.setValues(validationObj);
  }, [isFacebookPropertiesSet, modalState]);

  function handleClick(key) {
    validation.setFieldTouched(key, true);
  }

  function handleChange(key, value) {
    handleClick(key + ".name");
    validation.setFieldValue(key, { ...value, isFeed: false });
  }

  function handleTextboxChange(e, key) {
    validation.setFieldTouched(`${key}.value`, true);
    validation.setFieldValue(`${key}.value`, e.target.value);
  }

  function closeModal(e) {
    e ? e.preventDefault() : null;
    dispatch(toggleFacebookModal());
    setTimeout(() => {
      validation.handleReset();
    }, 200);
  }

  async function bindFeed(e, key) {
    e.preventDefault();
    if (currentVersion.hasOwnProperty('feeds') && Array.isArray(currentVersion.feeds)) {
      dispatch(setSelectedPath(null));
      dispatch(setSelectPathModalKind('facebookProperty'));
      dispatch(setSelectedFacebookProperty(key));
      dispatch(toggleSelectPathModal());
    } else {
      dispatch(toggleFeedConnectionModal())
      dispatch(toggleFeedConnectionLoader());
      dispatch(setFeedsPage(1));
      dispatch(setFeedsLoading(false));
      dispatch(setFeedsShowLoader(false));
      dispatch(setFeedsHasMore(true));
      dispatch(setFeedsFirstLoad(false));
      await dispatch(getFeeds());
      dispatch(toggleFeedConnectionLoader());
    }
  }

  function showFeedValue(path) {
    const foundFeed = (connectedFeed.find(item => item.path === path) || '');
    if (foundFeed) {
      return foundFeed.values[0];
    } else {
      return '';
    }
  }

  return (
    <Modal isOpen={modalState} toggle={closeModal} centered size='lg'>
      <ModalHeader className='p-3' toggle={closeModal}>
        Facebook Properties
      </ModalHeader>
      <Form onSubmit={(e) => {
        e.preventDefault();
        validation.handleSubmit();
        return false;
      }}>
        <ModalBody>
          {/* <ErrorAlert /> */}
          <Row>
            <Col lg={12} md={12} sm={12}>
              <div className='mb-2'>
                <div className='w-100 d-flex justify-content-between mb-2'>
                  <Label htmlFor='categoryId' className='w-100 d-flex align-items-center mb-0'>ID</Label>
                  <button className={'btn btn-icon btn-sm ' + (validation.values.id?.isFeed ? 'btn-info' : 'btn-light')}
                    onClick={(e) => bindFeed(e, 'id')}>
                    <i className='mdi mdi-rss'></i>
                  </button>
                </div>
                <Input type='text'
                  name='id'
                  id='id'
                  className={"form-control " + classnames({ "is-valid": validation.touched.id?.value && !validation.errors.id?.value })}
                  onChange={(e) => handleTextboxChange(e, 'id')}
                  invalid={validation.touched.id?.value && validation.errors.id?.value ? true : false}
                  value={validation.values.id?.isFeed ? showFeedValue(validation.values.id?.value) : validation.values.id?.value || ""}
                  disabled={validation.values.id?.isFeed}
                />
                {validation.touched.id?.value && !validation.errors.id?.value ? (
                  <FormFeedback valid>Looks good</FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg={6} md={6} sm={12}>
              <div>
                <div className='w-100 d-flex justify-content-between mb-2'>
                  <Label htmlFor='title' className='w-100 d-flex align-items-center mb-0'>Title</Label>
                  <button className={'btn btn-icon btn-sm ' + (validation.values.title?.isFeed ? 'btn-info' : 'btn-light')}
                    onClick={(e) => bindFeed(e, 'title')}>
                    <i className='mdi mdi-rss'></i>
                  </button>
                </div>
                <Input type='text'
                  name='title'
                  id='title'
                  className={"form-control " + classnames({ "is-valid": validation.touched.title?.value && !validation.errors.title?.value })}
                  placeholder='Enter Title'
                  onChange={(e) => handleTextboxChange(e, 'title')}
                  invalid={validation.touched.title?.value && validation.errors.title?.value ? true : false}
                  value={validation.values.title?.isFeed ? showFeedValue(validation.values.title?.value) : validation.values.title?.value || ""}
                  disabled={validation.values.title?.isFeed}
                />
                {validation.touched.title?.value && !validation.errors.title?.value ? (
                  <FormFeedback valid>Looks good</FormFeedback>
                ) : null}
                {validation.touched.title?.value && validation.errors.title?.value ? (
                  <FormFeedback type="invalid">{validation.errors.title?.value}</FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col lg={6} md={6} sm={12}>
              <div>
                <div className='w-100 d-flex justify-content-between mb-2'>
                  <Label htmlFor='availability' className='w-100 d-flex align-items-center mb-0'>Availability</Label>
                  <button className={'btn btn-icon btn-sm ' + (validation.values.availability?.isFeed ? 'btn-info' : 'btn-light')}
                    onClick={(e) => bindFeed(e, 'availability')}>
                    <i className='mdi mdi-rss'></i>
                  </button>
                </div>
                <CustomSelect placeholder='Select Availability'
                  className='form-select'
                  validation={classnames({
                    "is-valid": validation.touched.availability?.name && !validation.errors.availability?.name,
                    "is-invalid": validation.touched.availability?.name && validation.errors.availability?.name
                  })}
                  onChange={(item) => handleChange('availability', item)}
                  onClick={(item) => handleClick('availability.name')}
                  options={availabilityOption}
                  value={(validation.values.availability?.name)}
                  disabled={validation.values.availability?.isFeed}
                />
                {validation.touched.availability?.name && !validation.errors.availability?.name ? (
                  <FormFeedback valid>Looks good</FormFeedback>
                ) : null}
                {validation.touched.availability?.name && validation.errors.availability?.name ? (
                  <FormFeedback type="invalid">{validation.errors.availability?.name}</FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>

          <Row className='mt-2'>
            <Col lg={6} md={6} sm={12}>
              <div>
                <div className='w-100 d-flex justify-content-between mb-2'>
                  <Label htmlFor='description' className='w-100 d-flex align-items-center mb-0'>Description</Label>
                  <button className={'btn btn-icon btn-sm ' + (validation.values.description?.isFeed ? 'btn-info' : 'btn-light')}
                    onClick={(e) => bindFeed(e, 'description')}>
                    <i className='mdi mdi-rss'></i>
                  </button>
                </div>
                <Input type='text'
                  name='description'
                  id='description'
                  className={"form-control " + classnames({ "is-valid": validation.touched.description?.value && !validation.errors.description?.value })}
                  placeholder='Enter Description'
                  onChange={e => handleTextboxChange(e, 'description')}
                  invalid={validation.touched.description?.value && validation.errors.description?.value ? true : false}
                  value={validation.values.description?.isFeed ? showFeedValue(validation.values.description?.value) : validation.values.description?.value || ""}
                  disabled={validation.values.description?.isFeed}
                />
                {validation.touched.description?.value && !validation.errors.description?.value ? (
                  <FormFeedback valid>Looks good</FormFeedback>
                ) : null}
                {validation.touched.description?.value && validation.errors.description?.value ? (
                  <FormFeedback type="invalid">{validation.errors.description?.value}</FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col lg={6} md={6} sm={12}>
              <div>
                <div className='w-100 d-flex justify-content-between mb-2'>
                  <Label htmlFor='condition' className='w-100 d-flex align-items-center mb-0'>Condition</Label>
                  <button className={'btn btn-icon btn-sm ' + (validation.values.condition?.isFeed ? 'btn-info' : 'btn-light')}
                    onClick={(e) => bindFeed(e, 'condition')}>
                    <i className='mdi mdi-rss'></i>
                  </button>
                </div>
                <CustomSelect placeholder='Select Condition'
                  className='form-select'
                  validation={classnames({
                    "is-valid": validation.touched.condition?.name && !validation.errors.condition?.name,
                    "is-invalid": validation.touched.condition?.name && validation.errors.condition?.name
                  })}
                  onChange={(item) => handleChange('condition', item)}
                  onClick={() => handleClick('condition.name')}
                  options={conditionOption}
                  value={(validation.values.condition?.name)}
                  disabled={validation.values.condition?.isFeed}
                />
                {validation.touched.condition?.name && !validation.errors.condition?.name ? (
                  <FormFeedback valid>Looks good</FormFeedback>
                ) : null}
                {validation.touched.condition?.name && validation.errors.condition?.name ? (
                  <FormFeedback type="invalid">{validation.errors.condition?.name}</FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>

          <Row className='mt-2'>

            <Col lg={6} md={6} sm={12}>
              <div>
                <div className='w-100 d-flex justify-content-between mb-2'>
                  <Label htmlFor='price' className='w-100 d-flex align-items-center mb-0'>Price</Label>
                  <button className={'btn btn-icon btn-sm ' + (validation.values.price?.isFeed ? 'btn-info' : 'btn-light')}
                    onClick={(e) => bindFeed(e, 'price')}>
                    <i className='mdi mdi-rss'></i>
                  </button>
                </div>
                <Input type='text'
                  name='price'
                  id='price'
                  className={"form-control " + classnames({ "is-valid": validation.touched.price?.value && !validation.errors.price?.value })}
                  placeholder='Enter Price'
                  onChange={(e) => handleTextboxChange(e, 'price')}
                  invalid={validation.touched.price?.value && validation.errors.price?.value ? true : false}
                  value={validation.values.price?.isFeed ? showFeedValue(validation.values.price?.value) : validation.values.price?.value || ""}
                  disabled={validation.values.price?.isFeed}
                />
                {validation.touched.price?.value && !validation.errors.price?.value ? (
                  <FormFeedback valid>Looks good</FormFeedback>
                ) : null}
                {validation.touched.price?.value && validation.errors.price?.value ? (
                  <FormFeedback type="invalid">{validation.errors.price?.value}</FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col lg={6} md={6} sm={12}>
              <div>
                <div className='w-100 d-flex justify-content-between mb-2'>
                  <Label htmlFor='link' className='w-100 d-flex align-items-center mb-0'>Link</Label>
                  <button className={'btn btn-icon btn-sm ' + (validation.values.link?.isFeed ? 'btn-info' : 'btn-light')}
                    onClick={(e) => bindFeed(e, 'link')}>
                    <i className='mdi mdi-rss'></i>
                  </button>
                </div>
                <Input type='text'
                  name='link'
                  id='link'
                  className={"form-control " + classnames({ "is-valid": validation.touched.link?.value && !validation.errors.link?.value })}
                  placeholder='Enter Link'
                  onChange={(e) => handleTextboxChange(e, 'link')}
                  invalid={validation.touched.link?.value && validation.errors.link?.value ? true : false}
                  value={validation.values.link?.isFeed ? showFeedValue(validation.values.link?.value) : validation.values.link?.value || ""}
                  disabled={validation.values.link?.isFeed}
                />
                {validation.touched.link?.value && !validation.errors.link?.value ? (
                  <FormFeedback valid>Looks good</FormFeedback>
                ) : null}
                {validation.touched.link?.value && validation.errors.link?.value ? (
                  <FormFeedback type="invalid">{validation.errors.link?.value}</FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>
          <Row className='mt-2'>
            <Col lg={6} md={6} sm={12}>
              <div className='d-flex flex-column justify-content-end'>
                <div className='w-100 d-flex justify-content-between mb-2'>
                  <Label htmlFor='interval' className='w-100 d-flex align-items-center mb-0'>Interval</Label>
                </div>
                <CustomSelect placeholder='Select Interval'
                  className='form-select'
                  validation={classnames({
                    "is-valid": validation.touched.interval?.name && !validation.errors.interval?.name,
                    "is-invalid": validation.touched.interval?.name && validation.errors.interval?.name
                  })}
                  onChange={(item) => handleChange('interval', item)}
                  onClick={() => handleClick('interval.name')}
                  options={intervalOption}
                  value={(validation.values.interval?.name)}
                />
                {validation.touched.interval?.name && !validation.errors.interval?.name ? (
                  <FormFeedback valid>Looks good</FormFeedback>
                ) : null}
                {validation.touched.interval?.name && validation.errors.interval?.name ? (
                  <FormFeedback type="invalid">{validation.errors.interval?.name}</FormFeedback>
                ) : null}
              </div>
            </Col>
            <Col lg={6} md={6} sm={12}>
              <div>
                <div className='w-100 d-flex justify-content-between mb-2'>
                  <Label htmlFor='limit' className='w-100 d-flex align-items-center mb-0'>Limit</Label>
                </div>
                <Input type='text'
                  name='limit'
                  id='limit'
                  className={"form-control " + classnames({ "is-valid": validation.touched.limit && !validation.errors.limit })}
                  placeholder='Enter Limit'
                  // onChange={(e) => handleTextboxChange(e, 'limit')}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={validation.touched.limit && validation.errors.limit ? true : false}
                  value={validation.values.limit || ""} />
                {validation.touched.limit && !validation.errors.limit ? (
                  <FormFeedback valid>Looks good</FormFeedback>
                ) : null}
                {validation.touched.limit && validation.errors.limit ? (
                  <FormFeedback type="invalid">{validation.errors.limit}</FormFeedback>
                ) : null}
              </div>
            </Col>
          </Row>

        </ModalBody>
        <ModalFooter>
          <button className='btn btn-light' onClick={closeModal}>Close</button>
          <button type='submit' className='btn btn-success'>
            {isFacebookPropertiesSet ? 'Update' : 'Create'} Properties
          </button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default FacebookModal
